import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Maximize, Gauge } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const MONO = { fontFamily: "'JetBrains Mono', monospace" };
const SCANLINES = { background: "repeating-linear-gradient(0deg, rgba(148,163,184,0.045) 0 1px, transparent 1px 3px)" };
const GRID = {
  backgroundImage: "linear-gradient(rgba(34,211,238,0.055) 1px, transparent 1px), linear-gradient(90deg, rgba(34,211,238,0.055) 1px, transparent 1px)",
  backgroundSize: "44px 44px",
};

const COUNTERS = [
  { key: "active", label: "With you now", cls: "text-cyan-400" },
  { key: "high", label: "High priority", cls: "text-rose-500", alert: true },
  { key: "discharged", label: "Discharged", cls: "text-slate-300" },
  { key: "treated", label: "Treated total", cls: "text-emerald-400" },
  { key: "lost_with_us", label: "Lost children", cls: "text-amber-400", alert: true },
];

const fmtDay = (iso) =>
  iso ? new Date(`${iso}T00:00:00`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }) : "";

export default function ClientPortal() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [clock, setClock] = useState(new Date());

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/event-numbers");
      setData(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    const c = setInterval(() => setClock(new Date()), 1000);
    return () => { clearInterval(t); clearInterval(c); };
  }, [load]);

  const fullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    else document.documentElement.requestFullscreen().catch(() => {});
  };

  const events = data?.events || [];

  return (
    <div className="min-h-screen bg-[#020617] relative" data-testid="client-portal-page">
      <div className="absolute inset-0" style={GRID} />
      <div className="absolute inset-0 pointer-events-none" style={SCANLINES} />
      <div className="relative z-10 px-4 sm:px-8 py-5 max-w-[1800px] mx-auto">
        <header className="flex items-center justify-between flex-wrap gap-3 border-b border-cyan-500/20 pb-4 mb-6">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/admin")} data-testid="client-portal-back"
              className="h-10 w-10 rounded-lg border border-cyan-500/25 text-cyan-400 hover:bg-cyan-500/10 transition-colors grid place-items-center">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <div className="h-11 w-11 rounded-xl bg-cyan-500/10 border border-cyan-500/30 grid place-items-center">
              <Gauge className="h-5 w-5 text-cyan-400" />
            </div>
            <div>
              <h1 className="text-2xl lg:text-3xl font-black uppercase tracking-tight text-slate-100" style={MONO} data-testid="client-portal-title">
                Client Portal
              </h1>
              <p className="text-[11px] uppercase tracking-[0.3em] text-cyan-400" style={MONO}>
                Live event numbers · view only
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 sm:gap-5">
            <span className="hidden sm:flex items-center gap-2 text-xs text-emerald-400 uppercase tracking-widest" style={MONO}>
              <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" /> Live
            </span>
            <span className="text-sm text-slate-300 tabular-nums" style={MONO} data-testid="client-portal-clock">
              {clock.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}{" "}
              {clock.toLocaleTimeString("en-GB", { hour12: false })}
            </span>
            <button onClick={fullscreen} data-testid="client-portal-fullscreen" title="Fullscreen wall mode"
              className="h-10 w-10 rounded-lg border border-cyan-500/25 text-cyan-400 hover:bg-cyan-500/10 transition-colors grid place-items-center">
              <Maximize className="h-4 w-4" />
            </button>
          </div>
        </header>

        {data === null ? (
          <p className="text-sm text-slate-400" style={MONO}>Loading…</p>
        ) : events.length === 0 ? (
          <div className="rounded-2xl border border-cyan-500/20 bg-slate-900/80 p-10 text-center" data-testid="client-portal-empty">
            <p className="text-slate-300 text-sm" style={MONO}>No active events on the board.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {events.map((ev) => {
              const hot = ev.counts.high > 0 || ev.counts.lost_with_us > 0;
              return (
                <div key={ev.id} data-testid={`portal-numbers-${ev.id}`}
                  className={`rounded-2xl border bg-slate-900/90 p-6 ${hot ? "border-rose-500/40 ring-1 ring-rose-500/30 shadow-[0_0_18px_rgba(225,29,72,0.25)]" : "border-cyan-500/20"}`}>
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <h2 className="text-xl font-bold uppercase tracking-wide text-slate-100 truncate" style={MONO}>{ev.name}</h2>
                    <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse shrink-0 mt-2" />
                  </div>
                  <p className="text-xs text-slate-400 mb-1">{ev.venue || "Venue TBC"}</p>
                  <p className="text-[11px] text-slate-500 mb-5">
                    {ev.event_date ? fmtDay(ev.event_date) : "No date"}
                    {ev.end_date && ev.end_date > ev.event_date ? ` → ${fmtDay(ev.end_date)}` : ""}
                    {ev.times ? ` · ${ev.times}` : ""}
                  </p>
                  <div className="grid grid-cols-5 gap-2">
                    {COUNTERS.map((c) => (
                      <div key={c.key} className="text-center">
                        <p className={`text-3xl lg:text-4xl font-black tabular-nums ${c.cls} ${c.alert && ev.counts[c.key] > 0 ? "animate-pulse" : ""}`}
                          style={MONO} data-testid={`portal-${c.key}-${ev.id}`}>
                          {ev.counts[c.key]}
                        </p>
                        <p className="text-[9px] uppercase tracking-wider text-slate-500 leading-tight mt-1">{c.label}</p>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
